import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, Container, Paper, Typography, Stack, ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import DemoNavHeader from './components/DemoNavHeader';
import DemoExamDisplay from './components/DemoExamDisplay';
import DemoQuizInterface from './components/DemoQuizInterface';
import EmailCaptureModal from '../LandingStudent/components/EmailCaptureModal';
import analytics from '../../services/analytics';
import demoAnalytics from '../../services/demoAnalytics';
import { getStudentLandingThemeOverrides } from '../LandingStudent/styles/landingThemeConfig';
import { getDemoContainerProps, getPageBackgroundStyles } from '../LandingStudent/styles/demoContainerStyles';

const DemoViewerPage = () => {
  const { examId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Create the same theme used in StudentLandingPage
  // Get color theme from localStorage (for dev mode)
  const colorTheme = process.env.NODE_ENV === 'development' 
    ? localStorage.getItem('devColorTheme') || 'teal'
    : 'teal';
  const theme = createTheme(getStudentLandingThemeOverrides(colorTheme));
  const [examData, setExamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  
  // Determine if this is quiz mode based on URL
  const isQuizMode = location.pathname.includes('/quiz/');
  
  useEffect(() => {
    // Track sub-page view
    analytics.track('demo_subpage_viewed', {
      exam_id: examId,
      mode: isQuizMode ? 'quiz' : 'view',
      timestamp: new Date().toISOString()
    });
  }, [examId, isQuizMode]);
  
  useEffect(() => {
    const loadExamData = () => {
      try {
        // Try to load exam data from sessionStorage
        const storedData = sessionStorage.getItem(`demo_exam_${examId}`);
        
        if (storedData) {
          const data = JSON.parse(storedData);
          setExamData(data);
          
          // Initialize demo analytics
          demoAnalytics.trackDemoStart(isQuizMode ? 'quiz' : 'exam', data);
          
          setLoading(false);
        } else {
          // If no stored data, redirect back to landing page
          setError('Exam data not found. Please generate a new exam.');
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 3000);
        }
      } catch (err) {
        console.error('Error loading exam data:', err);
        setError('Failed to load exam data.');
        setLoading(false);
      }
    };
    
    if (examId) {
      loadExamData();
    } else {
      setError('Invalid exam ID.');
      setLoading(false);
    }
  }, [examId, navigate]);
  
  const handleCTAClick = (ctaType, position = null) => {
    // Track CTA clicks in PostHog
    analytics.track('demo_cta_clicked', {
      exam_id: examId,
      mode: isQuizMode ? 'quiz' : 'view',
      cta_type: ctaType,
      position: position,
      timestamp: new Date().toISOString()
    });
    
    // Track CTA clicks in demo analytics
    demoAnalytics.trackCTAClick(ctaType);
    
    // Open email capture modal
    setEmailModalOpen(true);
  };
  
  const handleBackToLanding = () => {
    analytics.track('demo_back_to_landing', {
      exam_id: examId,
      mode: isQuizMode ? 'quiz' : 'view',
      timestamp: new Date().toISOString()
    });
    
    navigate('/', { replace: true });
  };
  
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }
  
  // Get centralized styling configurations
  const demoContainerProps = getDemoContainerProps({ 
    mode: 'subpage', 
    urlText: isQuizMode ? 'quiz.examr.ai' : 'exam.examr.ai',
    colorTheme: colorTheme
  });
  const pageStyles = getPageBackgroundStyles({ mode: 'subpage', colorTheme: colorTheme });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      <Box sx={pageStyles.pageBackground}>
        <Container maxWidth="xl" sx={pageStyles.containerWrapper}>
          <Box sx={demoContainerProps.outerWrapper}>
            <Paper
              elevation={0}
              sx={demoContainerProps.demoContainer}
            >
              {/* Demo header bar - matching original exactly */}
              <Box sx={demoContainerProps.windowHeader}>
                <Stack direction="row" spacing={1}>
                  <Box sx={demoContainerProps.windowButtons.red} />
                  <Box sx={demoContainerProps.windowButtons.yellow} />
                  <Box sx={demoContainerProps.windowButtons.green} />
                </Stack>
                <Typography 
                  variant="caption" 
                  sx={demoContainerProps.windowUrl}
                >
                  {demoContainerProps.urlText}
                </Typography>
              </Box>

              {/* Demo content */}
              <Box sx={demoContainerProps.demoContent}>
                <DemoNavHeader 
                  onCTAClick={(position) => handleCTAClick('header', position)}
                  onBackToLanding={handleBackToLanding}
                  isQuizMode={isQuizMode}
                />
                
                <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                  {isQuizMode ? (
                    <DemoQuizInterface 
                      examData={examData}
                      onCTAClick={handleCTAClick}
                      onBackToLanding={handleBackToLanding}
                      demoAnalytics={demoAnalytics}
                    />
                  ) : (
                    <DemoExamDisplay 
                      examData={examData}
                      onCTAClick={handleCTAClick}
                      onBackToLanding={handleBackToLanding}
                      demoAnalytics={demoAnalytics}
                    />
                  )}
                </Box>
              </Box>

              {/* Glow effect - DISABLED for testing */}
              {/* <Box sx={demoContainerProps.glowEffect} /> */}
            </Paper>
          </Box>
        </Container>
        
        {/* Email Capture Modal */}
        <EmailCaptureModal
          open={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
          source={isQuizMode ? 'demo_quiz' : 'demo_exam'}
          headlineVariant="demo" // Demo pages use their own variant
          ctaVariant="variant_a" // Default CTA variant for demo pages
          demoAnalytics={demoAnalytics} // Pass demo analytics for signup data
        />
      </Box>
    </ThemeProvider>
  );
};

export default DemoViewerPage;
